import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Restaurant,
  LocalShipping,
  Event,
  TableRestaurant,
  Star,
  People,
  Schedule,
  LocationOn,
} from '@mui/icons-material';
import TableOrderLayout from '../../components/user/TableOrderLayout';

const AboutPage = () => {
  const features = [
    {
      icon: <Restaurant />,
      title: "Authentic Cuisine",
      description: "We serve authentic dishes prepared with traditional recipes and fresh ingredients sourced locally."
    },
    {
      icon: <LocalShipping />,
      title: "Fast Delivery",
      description: "Quick and reliable delivery service to bring delicious food right to your doorstep."
    },
    {
      icon: <Event />,
      title: "Catering Services",
      description: "Professional catering for events, parties, and special occasions with customizable menus."
    },
    {
      icon: <TableRestaurant />,
      title: "Table Reservations",
      description: "Book your table in advance for a perfect dining experience in our comfortable restaurant."
    }
  ];

  const stats = [
    { number: "500+", label: "Happy Customers" },
    { number: "50+", label: "Menu Items" },
    { number: "5+", label: "Years Experience" },
    { number: "24/7", label: "Customer Support" }
  ];

  const team = [
    {
      name: "Chef Maria Rodriguez",
      role: "Head Chef",
      description: "With over 15 years of experience in culinary arts, Chef Maria brings authentic flavors to every dish."
    },
    {
      name: "John Smith",
      role: "Restaurant Manager",
      description: "Ensuring exceptional service and memorable dining experiences for all our guests."
    },
    {
      name: "Sarah Johnson",
      role: "Catering Director",
      description: "Specializing in creating perfect menus for special events and corporate functions."
    }
  ];

  return (
    <TableOrderLayout 
      title="About Us" 
      subtitle="Discover our story, passion, and commitment to exceptional dining"
      showBackButton={true}
      showCart={false}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        {/* Hero Section */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            fontWeight="bold" 
            gutterBottom 
            color="primary"
            sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}
          >
            Welcome to Our Restaurant
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            paragraph 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto',
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
              lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 }
            }}
          >
            Since 2019, we have been dedicated to providing exceptional dining experiences through 
            authentic cuisine, warm hospitality, and innovative culinary techniques. Our passion for 
            food and commitment to quality has made us a beloved destination for food lovers.
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: { xs: 0.5, sm: 1 }, 
            mt: 2,
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} sx={{ color: '#ffc107', fontSize: { xs: 20, sm: 24, md: 28 } }} />
            ))}
            <Typography 
              variant="h6" 
              sx={{ 
                ml: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
              }}
            >
              4.9/5 Rating
            </Typography>
          </Box>
        </Paper>

        {/* Stats Section */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card elevation={1} sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
                <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                  <Typography 
                    variant="h3" 
                    fontWeight="bold" 
                    color="primary"
                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}
                  >
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Our Story */}
        <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Our Story
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="body1" paragraph>
                Founded in 2019 by a passionate group of culinary enthusiasts, our restaurant began 
                as a small family business with a simple mission: to serve authentic, delicious food 
                that brings people together.
              </Typography>
              <Typography variant="body1" paragraph>
                Over the years, we have grown from a local favorite to a recognized name in the 
                culinary scene, while maintaining our commitment to quality, authenticity, and 
                exceptional service.
              </Typography>
              <Typography variant="body1" paragraph>
                Today, we continue to innovate while honoring traditional cooking methods, ensuring 
                that every dish tells a story and every meal creates lasting memories.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                height: 300, 
                background: 'linear-gradient(45deg, #f5f5f5 30%, #e0e0e0 90%)',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Restaurant sx={{ fontSize: 80, color: 'primary.main' }} />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Features */}
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          What We Offer
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card elevation={1} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      {feature.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Our Values */}
        <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Our Values
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <People sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Community
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We believe in building strong relationships with our community and supporting local suppliers.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Star sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Quality
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We never compromise on quality, using only the finest ingredients and traditional cooking methods.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Schedule sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Innovation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We continuously innovate while respecting tradition, creating unique dining experiences.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Team Section */}
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Meet Our Team
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {team.map((member, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card elevation={1}>
                <CardContent>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ 
                      width: 80, 
                      height: 80, 
                      mx: 'auto', 
                      mb: 2,
                      bgcolor: 'primary.main',
                      fontSize: '2rem'
                    }}>
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {member.name}
                    </Typography>
                    <Chip 
                      label={member.role} 
                      color="primary" 
                      variant="outlined" 
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {member.description}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Contact Info */}
        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Visit Us
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">Address</Typography>
                  <Typography variant="body2" color="text.secondary">
                    123 Main Street, Downtown<br />
                    City, State 12345
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">Hours</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monday - Friday: 11:00 AM - 10:00 PM<br />
                    Saturday - Sunday: 10:00 AM - 11:00 PM
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" paragraph>
                We look forward to welcoming you to our restaurant and sharing our passion for 
                exceptional food and hospitality. Whether you're joining us for a casual meal, 
                a special celebration, or a business event, we promise an unforgettable experience.
              </Typography>
              <Typography variant="body1">
                Thank you for being part of our journey and for choosing us as your dining destination.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </TableOrderLayout>
  );
};

export default AboutPage;
